import AddIcon from '@mui/icons-material/Add';
import KeyIcon from '@mui/icons-material/Key';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  clearAccountCredential,
  createAdminAccount,
  createRemoteAccount,
  getAdminAccount,
  listAdminAccounts,
  listRemoteAccounts,
  listTenants,
  setAccountCredential,
} from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { AdminFlowisAccount } from '../../api/types';
import { ErrorBlock, EmptyBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { AccountIdentifierFields } from './AccountIdentifierFields';
import { AdminSectionTabs } from './AdminSectionTabs';
import { emptyIdentifiers, identifiersToRequest, type IdentifierFormState } from './accountIdentifiers';
import { useAccountAssignment } from './useAccountAssignment';

type PoolFilter = 'all' | 'unassigned' | 'assigned';

const filterToQuery: Record<PoolFilter, boolean | undefined> = {
  all: undefined,
  unassigned: true,
  assigned: false,
};

type RegisterMode = 'existing' | 'create';

/**
 * Registering an account covers two different situations: one Flowis already has, whose identifier we
 * are given and whose details are then read from there, and one that does not exist yet, which Flowis
 * has to create before it has an identifier at all. Only the second asks for identifiers — the first
 * would just be retyping what Flowis already holds.
 */
function RegisterDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [mode, setMode] = useState<RegisterMode>('existing');
  const [accountId, setAccountId] = useState('');
  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [tenantGid, setTenantGid] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [identifiers, setIdentifiers] = useState<IdentifierFormState>(emptyIdentifiers);

  const tenants = useQuery({
    queryKey: queryKeys.admin.tenants(),
    queryFn: ({ signal }) => listTenants(signal),
  });

  const creatingAtGate = mode === 'create';

  const mutation = useMutation({
    mutationFn: () => {
      const fields = identifiersToRequest(identifiers);

      return creatingAtGate
        ? createRemoteAccount({
            name: name.trim(),
            // The gate insists on both; everything else it treats as optional.
            taxId: identifiers.taxId.trim(),
            countryCode: identifiers.countryCode.trim(),
            registrationNumber: fields.registrationNumber,
            vatId: fields.vatId,
            participantId: fields.participantId,
            tenantGid: tenantGid === '' ? null : tenantGid,
            clientId: clientId.trim() === '' ? null : clientId.trim(),
            clientSecret: clientSecret.trim() === '' ? null : clientSecret.trim(),
          })
        : createAdminAccount({
            accountId: accountId.trim(),
            // Optional here: the name Flowis holds is used when this is left empty.
            name: name.trim() === '' ? null : name.trim(),
            enabled,
            tenantGid: tenantGid === '' ? null : tenantGid,
            clientId: clientId.trim() === '' ? null : clientId.trim(),
            clientSecret: clientSecret.trim() === '' ? null : clientSecret.trim(),
          });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(
        creatingAtGate
          ? t('admin.accounts.registerDialog.created')
          : t('admin.accounts.registerDialog.registered'),
      );
      onClose();
    },
    onError: toastError,
  });

  const incomplete = creatingAtGate
    ? name.trim() === '' || identifiers.taxId.trim() === '' || identifiers.countryCode.trim() === ''
    : accountId.trim() === '';

  return (
    <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.accounts.registerDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={mode}
            onChange={(_, next: RegisterMode | null) => next && setMode(next)}
          >
            <ToggleButton value="existing">{t('admin.accounts.registerDialog.modeExisting')}</ToggleButton>
            <ToggleButton value="create">{t('admin.accounts.registerDialog.modeCreate')}</ToggleButton>
          </ToggleButtonGroup>

          {creatingAtGate ? (
            <Typography variant="body2" color="text.secondary">
              {t('admin.accounts.registerDialog.createIntro')}
            </Typography>
          ) : (
            <>
              <TextField
                label={t('admin.accounts.columns.accountId')}
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
                required
                helperText={t('admin.accounts.accountIdHelp')}
              />
              <Typography variant="body2" color="text.secondary">
                {t('admin.accounts.registerDialog.existingIntro')}
              </Typography>
            </>
          )}
          <TextField
            label={t('columns.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required={creatingAtGate}
            helperText={
              creatingAtGate ? t('admin.accounts.nameOperatorsHelp') : t('admin.accounts.nameOptionalHelp')
            }
          />
          <TextField
            select
            label={t('tenants.label')}
            value={tenantGid}
            onChange={(event) => setTenantGid(event.target.value)}
            helperText={t('admin.accounts.registerDialog.tenantHelp')}
          >
            <MenuItem value="">
              <em>{t('admin.accounts.unassignedChip')}</em>
            </MenuItem>
            {(tenants.data ?? []).map((tenant) => (
              <MenuItem key={tenant.tenantGid} value={tenant.tenantGid}>
                {tenant.name}
              </MenuItem>
            ))}
          </TextField>
          {/* Flowis offers two ways in: the global API pair, or a pair issued for one account. */}
          <TextField
            label={t('admin.accounts.clientId')}
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
            helperText={
              creatingAtGate
                ? t('admin.accounts.registerDialog.clientIdHelpCreate')
                : t('admin.accounts.registerDialog.clientIdHelpExisting')
            }
          />
          <TextField
            label={t('admin.accounts.clientSecret')}
            type="password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            helperText={t('admin.accounts.registerDialog.clientSecretHelp')}
          />
          {!creatingAtGate && (
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />}
              label={t('states.enabled')}
            />
          )}
          {/* Only when creating: an account Flowis already holds arrives with its identifiers. */}
          {creatingAtGate && <AccountIdentifierFields value={identifiers} onChange={setIdentifiers} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || incomplete}
        >
          {creatingAtGate ? t('actions.create') : t('actions.register')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Browses the accounts the gate itself holds, so an identifier never has to be copied by hand. */
function BrowseGateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const remote = useQuery({
    queryKey: queryKeys.admin.remoteAccounts(),
    queryFn: ({ signal }) => listRemoteAccounts(signal),
    enabled: open,
  });

  const register = useMutation({
    mutationFn: ({ accountId, name }: { accountId: string; name: string | null }) =>
      createAdminAccount({
        accountId,
        name,
        enabled: true,
        tenantGid: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.accounts.browseDialog.registeredToPool'));
    },
    onError: toastError,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{t('admin.accounts.browseDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* The global API pair is the identity browsing here; an account only its own pair can
              see is registered through the register dialog with that pair typed in. */}
          {remote.isPending ? (
            <LoadingBlock label={t('flowis.asking')} />
          ) : remote.isError ? (
            <ErrorBlock error={remote.error} onRetry={() => void remote.refetch()} />
          ) : remote.data.length === 0 ? (
            <EmptyBlock message={t('admin.accounts.browseDialog.empty')} />
          ) : (
            <Paper variant="outlined">
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('columns.name')}</TableCell>
                      <TableCell>{t('admin.accounts.columns.participant')}</TableCell>
                      <TableCell>{t('admin.accounts.columns.accountId')}</TableCell>
                      <TableCell>{t('admin.accounts.columns.ico')}</TableCell>
                      <TableCell>{t('admin.accounts.columns.dic')}</TableCell>
                      <TableCell>{t('columns.status')}</TableCell>
                      <TableCell>{t('admin.accounts.columns.registeredHere')}</TableCell>
                      <TableCell align="right">{t('columns.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {remote.data.map((account, index) => (
                      <TableRow key={account.accountId ?? `row-${index}`} hover>
                        <TableCell>{account.name ?? '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{account.participantId ?? '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{account.accountId ?? '—'}</TableCell>
                        <TableCell>{account.registrationNumber ?? '—'}</TableCell>
                        <TableCell>{account.taxId ?? '—'}</TableCell>
                        <TableCell>{account.status ?? '—'}</TableCell>
                        <TableCell>
                          {account.accountGid ? (
                            <Chip
                              size="small"
                              color="success"
                              label={account.tenantName ?? t('admin.accounts.unassignedChip')}
                            />
                          ) : (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={t('admin.accounts.browseDialog.notRegistered')}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            disabled={
                              account.accountId === null || account.accountGid !== null || register.isPending
                            }
                            onClick={() => {
                              // An account the gate listed without its identifier cannot be registered.
                              if (account.accountId !== null)
                                register.mutate({ accountId: account.accountId, name: account.name });
                            }}
                          >
                            {t('actions.register')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.close')}</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Hands an account its own Flowis API client pair, or returns it to the global one — the same two
 * options Flowis itself offers. The pair is proven against the gate before it is stored, and the
 * secret is never shown back.
 */
function CredentialDialog({ account, onClose }: { account: AdminFlowisAccount; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  const save = useMutation({
    mutationFn: () =>
      setAccountCredential(account.accountGid, {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.accounts.credentialDialog.savedOwn'));
      onClose();
    },
    onError: toastError,
  });

  const clear = useMutation({
    mutationFn: () => clearAccountCredential(account.accountGid),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.accounts.credentialDialog.savedGlobal'));
      onClose();
    },
    onError: toastError,
  });

  const pending = save.isPending || clear.isPending;

  return (
    <Dialog open onClose={pending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.accounts.credentialDialog.title', { name: account.name })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {account.hasOwnCredential
              ? t('admin.accounts.credentialDialog.hasOwn')
              : t('admin.accounts.credentialDialog.usesGlobal')}
          </Typography>
          <TextField
            label={t('admin.accounts.clientId')}
            value={clientId}
            onChange={(event) => setClientId(event.target.value)}
          />
          <TextField
            label={t('admin.accounts.clientSecret')}
            type="password"
            value={clientSecret}
            onChange={(event) => setClientSecret(event.target.value)}
            helperText={t('admin.accounts.credentialDialog.secretHelp')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {account.hasOwnCredential && (
          <Button color="warning" disabled={pending} onClick={() => clear.mutate()}>
            {t('admin.accounts.credentialDialog.useGlobal')}
          </Button>
        )}
        <Button onClick={onClose} disabled={pending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={pending || clientId.trim() === '' || clientSecret.trim() === ''}
          onClick={() => save.mutate()}
        >
          {t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Places a pooled account with a tenant. */
function AssignDialog({ account, onClose }: { account: AdminFlowisAccount; onClose: () => void }) {
  const [tenantGid, setTenantGid] = useState('');
  const { assign } = useAccountAssignment();
  const { t } = useTranslation();

  const tenants = useQuery({
    queryKey: queryKeys.admin.tenants(),
    queryFn: ({ signal }) => listTenants(signal),
  });

  return (
    <Dialog open onClose={assign.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('admin.accounts.assignDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin.accounts.assignDialog.body')}
          </Typography>
          <TextField
            select
            label={t('tenants.label')}
            value={tenantGid}
            onChange={(event) => setTenantGid(event.target.value)}
          >
            {(tenants.data ?? []).map((tenant) => (
              <MenuItem key={tenant.tenantGid} value={tenant.tenantGid}>
                {tenant.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={assign.isPending}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={tenantGid === '' || assign.isPending}
          onClick={() =>
            assign.mutate(
              { accountGid: account.accountGid, tenantGid },
              {
                onSuccess: onClose,
              },
            )
          }
        >
          {t('actions.assign')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function AccountsPage() {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();

  const [filter, setFilter] = useState<PoolFilter>('all');
  const [registerOpen, setRegisterOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [assigning, setAssigning] = useState<AdminFlowisAccount | null>(null);
  const [editingCredential, setEditingCredential] = useState<AdminFlowisAccount | null>(null);

  const { unassign } = useAccountAssignment();

  const accounts = useQuery({
    queryKey: queryKeys.admin.accounts(filterToQuery[filter]),
    queryFn: ({ signal }) => listAdminAccounts(filterToQuery[filter], signal),
  });

  const refresh = useMutation({
    mutationFn: (accountGid: string) => getAdminAccount(accountGid, true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.admin.all });
      toastSuccess(t('admin.accounts.refreshed'));
    },
    onError: toastError,
  });

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5">{t('admin.title')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('admin.accounts.description')}
        </Typography>
      </Box>

      <AdminSectionTabs />

      <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" flexWrap="wrap">
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filter}
          onChange={(_, next: PoolFilter | null) => next && setFilter(next)}
        >
          <ToggleButton value="all">{t('admin.accounts.filterAll')}</ToggleButton>
          <ToggleButton value="unassigned">{t('admin.accounts.filterUnassigned')}</ToggleButton>
          <ToggleButton value="assigned">{t('admin.accounts.filterAssigned')}</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<TravelExploreIcon />} onClick={() => setBrowseOpen(true)}>
            {t('admin.accounts.browse')}
          </Button>
          <Button startIcon={<AddIcon />} onClick={() => setRegisterOpen(true)}>
            {t('admin.accounts.register')}
          </Button>
        </Box>
      </Stack>

      <Alert severity="info">{t('admin.accounts.note')}</Alert>

      {accounts.isPending ? (
        <LoadingBlock label={t('admin.accounts.loading')} />
      ) : accounts.isError ? (
        <ErrorBlock error={accounts.error} onRetry={() => void accounts.refetch()} />
      ) : accounts.data.length === 0 ? (
        <EmptyBlock message={t('admin.accounts.empty')} />
      ) : (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('columns.name')}</TableCell>
                  <TableCell>{t('columns.tenant')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.participant')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.accountId')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.ico')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.dic')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.icdph')}</TableCell>
                  <TableCell>{t('columns.country')}</TableCell>
                  <TableCell>{t('admin.accounts.columns.credentials')}</TableCell>
                  <TableCell>{t('columns.enabled')}</TableCell>
                  <TableCell align="right">{t('columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.data.map((account) => (
                  <TableRow key={account.accountGid} hover>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>
                      {account.tenantName ? (
                        account.tenantName
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          variant="outlined"
                          label={t('admin.accounts.unassignedChip')}
                        />
                      )}
                    </TableCell>
                    {/* The address Peppol knows this sender by — the identifier that matters most. */}
                    <TableCell sx={{ fontFamily: 'monospace' }}>{account.participantId ?? '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{account.accountId}</TableCell>
                    <TableCell>{account.registrationNumber ?? '—'}</TableCell>
                    <TableCell>{account.taxId ?? '—'}</TableCell>
                    <TableCell>{account.vatId ?? '—'}</TableCell>
                    <TableCell>{account.countryCode ?? '—'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={account.hasOwnCredential ? 'info' : 'default'}
                        label={
                          account.hasOwnCredential
                            ? t('admin.accounts.ownPair')
                            : t('admin.accounts.globalPair')
                        }
                      />
                    </TableCell>
                    <TableCell>{account.enabled ? t('states.yes') : t('states.no')}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={t('admin.accounts.refreshTooltip')}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={refresh.isPending}
                            onClick={() => refresh.mutate(account.accountGid)}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('admin.accounts.credentialTooltip')}>
                        <IconButton size="small" onClick={() => setEditingCredential(account)}>
                          <KeyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {account.tenantGid === null ? (
                        <Tooltip title={t('admin.accounts.assignTooltip')}>
                          <IconButton size="small" onClick={() => setAssigning(account)}>
                            <LinkIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('admin.accounts.unassignTooltip')}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={unassign.isPending}
                              onClick={() =>
                                unassign.mutate({
                                  accountGid: account.accountGid,
                                  tenantGid: account.tenantGid!,
                                })
                              }
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {registerOpen && <RegisterDialog onClose={() => setRegisterOpen(false)} />}
      <BrowseGateDialog open={browseOpen} onClose={() => setBrowseOpen(false)} />
      {assigning && <AssignDialog account={assigning} onClose={() => setAssigning(null)} />}
      {editingCredential && (
        <CredentialDialog account={editingCredential} onClose={() => setEditingCredential(null)} />
      )}
    </Stack>
  );
}
